<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <!-- Header with Save Button -->
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h5 class="mb-0">{{ $t("dmp.systemOptions.title") }}</h5>
          <div class="d-flex gap-1">
            <b-button v-if="!isTemplateMode" variant="primary" size="sm" @click="saveSystemOptions" :disabled="isSaving">
              <b-spinner small v-if="isSaving" class="mr-25" />
              <feather-icon v-else icon="SaveIcon" size="14" class="mr-25" />
              {{ isSaving ? $t("actions.saving") : $t("actions.save") }}
            </b-button>
          </div>
        </div>

        <!-- Loading spinner -->
        <div v-if="isLoading" class="d-flex justify-content-center p-3">
          <b-spinner variant="primary" :label="$t('dmp.systemOptions.loadingSystemOptions')" />
          <span class="ml-2">{{ $t("dmp.systemOptions.loadingSystemOptions") }}</span>
        </div>

        <validation-observer ref="refFormObserver" v-else>

          <b-form @submit.prevent="saveSystemOptions">
            <b-row>
              <!-- Left Column -->
              <b-col cols="12" md="6">
                <!-- Options Card -->
                <b-card class="mb-1 border">
                  <h6 class="mb-2 font-weight-bold">
                    {{ $t("dmp.systemOptions.options") }}
                  </h6>

                  <b-form-group :label="$t('dmp.systemOptions.systemArmingType')" label-for="armingSystem">
                    <validation-provider #default="{ errors }" :name="$t('dmp.systemOptions.systemArmingType')"
                      rules="required">
                      <b-form-select id="armingSystem" v-model="systemOptions.armingSystem"
                        :options="armingSystemOptions" :state="errors.length > 0 ? false : null" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-checkbox v-model="systemOptions.instantArm" :disabled="isSaving" class="mb-1">
                    {{ $t("dmp.systemOptions.instantArm") }}
                  </b-form-checkbox>

                  <b-form-checkbox v-model="systemOptions.closingWait" :disabled="isSaving" class="mb-1">
                    {{ $t("dmp.systemOptions.closingWait") }}
                  </b-form-checkbox>

                  <b-form-checkbox v-model="systemOptions.resetSwingerBypass" :disabled="isSaving" class="mb-1">
                    {{ $t("dmp.systemOptions.resetSwingerBypass") }}
                  </b-form-checkbox>

                  <b-form-checkbox v-model="systemOptions.enableKeypadPanicKeys" :disabled="isSaving" class="mb-1">
                    {{ $t("dmp.systemOptions.enableKeypadPanicKeys") }}
                  </b-form-checkbox>

                  <b-form-checkbox v-model="systemOptions.enhancedZoneTest" :disabled="isSaving" class="mb-1">
                    {{ $t("dmp.systemOptions.enhancedZoneTest") }}
                  </b-form-checkbox>

                  <b-form-checkbox v-model="systemOptions.send16Characters" :disabled="isSaving" class="mb-1">
                    {{ $t("dmp.systemOptions.send16Characters") }}
                  </b-form-checkbox>

                  <b-form-checkbox v-model="systemOptions.useFalseAlarmQuestion" :disabled="isSaving" class="mb-1">
                    {{ $t("dmp.systemOptions.useFalseAlarmQuestion") }}
                  </b-form-checkbox>

                  <b-form-checkbox v-model="systemOptions.allowOwnUserCodeChange" :disabled="isSaving" class="mb-1">
                    {{ $t("dmp.systemOptions.allowOwnUserCodeChange") }}
                  </b-form-checkbox>

                  <b-form-checkbox v-model="systemOptions.panicSupervision" :disabled="isSaving" class="mb-1">
                    {{ $t("dmp.systemOptions.panicSupervision") }}
                  </b-form-checkbox>

                  <b-form-group :label="$t('dmp.systemOptions.keypadArmedLed')" label-for="keypadArmedLed"
                    class="mt-1 mb-0">
                    <validation-provider #default="{ errors }" :name="$t('dmp.systemOptions.keypadArmedLed')"
                      rules="required">
                      <b-form-select id="keypadArmedLed" v-model="systemOptions.keypadArmedLed"
                        :options="keypadArmedLedOptions" :state="errors.length > 0 ? false : null" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-card>

                <!-- Miscellaneous Options Card -->
                <b-card class="mb-1 border">
                  <h6 class="mb-2 font-weight-bold">
                    {{ $t("dmp.systemOptions.miscellaneousOptions") }}
                  </h6>

                  <b-form-group label="Trouble Audibles" label-for="troubleAudible">
                    <validation-provider #default="{ errors }" name="Trouble Audibles" rules="required">
                      <b-form-select id="troubleAudible" v-model="systemOptions.troubleAudible"
                        :options="troubleAudibleOptions" :state="errors.length > 0 ? false : null" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>


                  <b-form-group :label="$t('dmp.systemOptions.swingerBypassTrips')" label-for="swingerBypassTrips">
                    <validation-provider #default="{ errors }" :name="$t('dmp.systemOptions.swingerBypassTrips')"
                      rules="required|regex:^[0-7]$">
                      <b-form-input id="swingerBypassTrips" v-model="systemOptions.swingerBypassTrips" type="text"
                        :state="errors.length > 0 ? false : null" maxlength="1" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-group :label="$t('dmp.systemOptions.bypassLimit')" label-for="bypassLimit">
                    <validation-provider #default="{ errors }" :name="$t('dmp.systemOptions.bypassLimit')"
                      rules="required|regex:^[0-8]$">
                      <b-form-input id="bypassLimit" v-model="systemOptions.bypassLimit" type="text"
                        :state="errors.length > 0 ? false : null" maxlength="1" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-group :label="$t('dmp.systemOptions.weatherZipCode')" label-for="weatherZipCode">
                    <validation-provider #default="{ errors }" :name="$t('dmp.systemOptions.weatherZipCode')"
                      rules="regex:^[0-9]*$">
                      <b-form-input id="weatherZipCode" v-model="systemOptions.weatherZipCode" type="text"
                        :state="errors.length > 0 ? false : null" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <div class="pt-0 pb-1">
                    <b-form-checkbox v-model="systemOptions.occupiedPremises" :disabled="isSaving">
                      {{ $t("dmp.systemOptions.occupiedPremises") }}
                    </b-form-checkbox>
                  </div>


                  <b-form-group class="" :label="$t('dmp.systemOptions.eolResistor')" label-for="eolResistor">
                    <validation-provider #default="{ errors }" :name="$t('dmp.eolResistor')" rules="required">
                      <b-form-select id="eolResistor" v-model="systemOptions.eolResistor" :options="eolResistorOptions"
                        :state="errors.length > 0 ? false : null" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-checkbox v-model="systemOptions.useCelsiusTemperature" :disabled="isSaving">
                    {{ $t("dmp.systemOptions.useCelsiusTemperature") }}
                  </b-form-checkbox>

                </b-card>

                <!-- Languages Card -->
                <b-card class="mb-1 border">
                  <h6 class="mb-2 font-weight-bold">
                    {{ $t("dmp.systemOptions.languages.title") }}
                  </h6>

                  <b-form-group :label="$t('dmp.systemOptions.primaryProgrammingLang')"
                    label-for="primaryProgrammingLang">
                    <validation-provider #default="{ errors }" :name="$t('dmp.systemOptions.primaryProgrammingLang')"
                      rules="required">
                      <b-form-select id="primaryProgrammingLang" v-model="systemOptions.primaryProgrammingLang"
                        :options="programmingLangOptions" :state="errors.length > 0 ? false : null" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-group :label="$t('dmp.systemOptions.secondaryProgrammingLang')"
                    label-for="secondaryProgrammingLang">
                    <validation-provider #default="{ errors }" :name="$t('dmp.systemOptions.secondaryProgrammingLang')"
                      rules="required">
                      <b-form-select id="secondaryProgrammingLang" v-model="systemOptions.secondaryProgrammingLang"
                        :options="programmingLangOptions" :state="errors.length > 0 ? false : null" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-group :label="$t('dmp.systemOptions.primaryUserLang')" label-for="primaryUserLang">
                    <validation-provider #default="{ errors }" :name="$t('dmp.systemOptions.primaryUserLang')"
                      rules="required">
                      <b-form-select id="primaryUserLang" v-model="systemOptions.primaryUserLang"
                        :options="programmingLangOptions" :state="errors.length > 0 ? false : null" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-group :label="$t('dmp.systemOptions.secondaryUserLang')" label-for="secondaryUserLang"
                    class="mb-0">
                    <validation-provider #default="{ errors }" :name="$t('dmp.systemOptions.secondaryUserLang')"
                      rules="required">
                      <b-form-select id="secondaryUserLang" v-model="systemOptions.secondaryUserLang"
                        :options="programmingLangOptions" :state="errors.length > 0 ? false : null" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-card>
              </b-col>

              <!-- Right Column -->
              <b-col cols="12" md="6">
                <!-- Time Change Card -->
                <b-card class="mb-1 border">
                  <h6 class="mb-2 font-weight-bold">
                    {{ $t("dmp.systemOptions.timeChange") }}
                  </h6>

                  <b-form-group :label="$t('dmp.systemOptions.hoursFromGMT')" label-for="hoursFromGTM">
                    <validation-provider #default="{ errors }" :name="$t('dmp.systemOptions.hoursFromGMT')"
                      rules="required|numeric|min_value:0|max_value:23">
                      <b-form-input id="hoursFromGTM" v-model="systemOptions.hoursFromGTM" type="number"
                        :state="errors.length > 0 ? false : null" min="0" max="23" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-group :label="$t('dmp.systemOptions.timeChangeOption')" label-for="timeChange">
                    <validation-provider #default="{ errors }" :name="$t('dmp.eolResistor')" rules="required">
                      <b-form-select id="timeChange" v-model="systemOptions.timeChange" :options="timeChangeOptions"
                        :state="errors.length > 0 ? false : null" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-checkbox v-model="systemOptions.observeDST"
                    :disabled="isSaving || systemOptions.timeChange !== 'SECURECOM'" class="mb-0">
                    {{ $t("dmp.systemOptions.observeDST") }}
                  </b-form-checkbox>
                </b-card>

                <!-- Delays Card -->
                <b-card class="mb-1 border">
                  <h6 class="mb-2 font-weight-bold">
                    {{ $t("dmp.systemOptions.delays") }}
                  </h6>

                  <b-form-group :label="$t('dmp.systemOptions.zoneActivityHours')" label-for="zoneActivityHours">
                    <validation-provider #default="{ errors }" :name="$t('dmp.systemOptions.zoneActivityHours')"
                      rules="required|regex:^[0-9]$">
                      <b-form-input id="zoneActivityHours" v-model="systemOptions.zoneActivityHours" type="text"
                        :state="errors.length > 0 ? false : null" maxlength="1" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-group :label="$t('dmp.systemOptions.entryDelay1')" label-for="entryDelay1">
                    <validation-provider #default="{ errors }" :name="$t('dmp.systemOptions.entryDelay1')"
                      rules="required|numeric|min_value:20|max_value:250">
                      <b-form-input id="entryDelay1" v-model.number="systemOptions.entryDelay1" type="number"
                        :state="errors.length > 0 ? false : null" min="20" max="250" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-group :label="$t('dmp.systemOptions.entryDelay2')" label-for="entryDelay2">
                    <validation-provider #default="{ errors }" :name="$t('dmp.systemOptions.entryDelay2')"
                      rules="required|numeric|min_value:20|max_value:250">
                      <b-form-input id="entryDelay2" v-model.number="systemOptions.entryDelay2" type="number"
                        :state="errors.length > 0 ? false : null" min="20" max="250" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-group :label="$t('dmp.systemOptions.entryDelay3')" label-for="entryDelay3">
                    <validation-provider #default="{ errors }" :name="$t('dmp.systemOptions.entryDelay3')"
                      rules="required|numeric|min_value:20|max_value:250">
                      <b-form-input id="entryDelay3" v-model.number="systemOptions.entryDelay3" type="number"
                        :state="errors.length > 0 ? false : null" min="20" max="250" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-group :label="$t('dmp.systemOptions.entryDelay4')" label-for="entryDelay4">
                    <validation-provider #default="{ errors }" :name="$t('dmp.systemOptions.entryDelay4')"
                      rules="required|numeric|min_value:20|max_value:250">
                      <b-form-input id="entryDelay4" v-model.number="systemOptions.entryDelay4" type="number"
                        :state="errors.length > 0 ? false : null" min="20" max="250" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-group :label="$t('dmp.systemOptions.crossZoneTime')" label-for="crossZoneTime">
                    <validation-provider #default="{ errors }" :name="$t('dmp.systemOptions.crossZoneTime')"
                      rules="required|numeric|min_value:0|max_value:250">
                      <b-form-input id="crossZoneTime" v-model.number="systemOptions.crossZoneTime" type="number"
                        :state="errors.length > 0 ? false : null" min="0" max="250" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-group :label="$t('dmp.systemOptions.zoneRetardDelay')" label-for="zoneRetardDelay">
                    <validation-provider #default="{ errors }" :name="$t('dmp.systemOptions.zoneRetardDelay')"
                      rules="required|numeric|min_value:0|max_value:250">
                      <b-form-input id="zoneRetardDelay" v-model.number="systemOptions.zoneRetardDelay" type="number"
                        :state="errors.length > 0 ? false : null" min="0" max="250" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-group :label="$t('dmp.systemOptions.powerFailDelay')" label-for="powerFailDelay" class="mb-0">
                    <validation-provider #default="{ errors }" :name="$t('dmp.systemOptions.powerFailDelay')"
                      rules="required|numeric|min_value:0|max_value:15">
                      <b-form-input id="powerFailDelay" v-model.number="systemOptions.powerFailDelay" type="number"
                        :state="errors.length > 0 ? false : null" min="0" max="15" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-card>

                <!-- Wireless Card -->
                <b-card class="mb-1 border">
                  <h6 class="mb-2 font-weight-bold">
                    {{ $t("dmp.systemOptions.wireless") }}
                  </h6>

                  <b-form-group :label="$t('dmp.systemOptions.wirelessHouseCode')" label-for="wirelessHouseCode">
                    <validation-provider #default="{ errors }" :name="$t('dmp.systemOptions.wirelessHouseCode')"
                      rules="required|numeric|min_value:0|max_value:50">
                      <b-form-input id="wirelessHouseCode" v-model.number="systemOptions.wirelessHouseCode"
                        type="number" :state="errors.length > 0 ? false : null" min="0" max="50" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-checkbox v-model="systemOptions.detectWirelessJamming" :disabled="isSaving" class="mb-1">
                    {{ $t("dmp.systemOptions.detectWirelessJamming") }}
                  </b-form-checkbox>


                  <b-form-group :label="$t('dmp.systemOptions.encryptionSettings')" label-for="encryptionSettings">
                    <validation-provider #default="{ errors }" :name="$t('dmp.systemOptions.encryptionSettings')"
                      rules="required">
                      <b-form-select id="encryptionSettings" v-model="systemOptions.encryptionSettings"
                        :options="encryptionSettingsOptions" :state="errors.length > 0 ? false : null" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>


                  <b-form-group :label="$t('dmp.systemOptions.encryptionPassphrase')" label-for="Encryption Passphrase"
                    class="mb-0">
                    <validation-provider #default="{ errors }" :name="$t('dmp.systemOptions.encryptionPassphrase')">
                      <b-form-input id="encryptionPassphrase" v-model="systemOptions.encryptionPassphrase"
                        type="password" :state="errors.length > 0 ? false : null" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-card>

                <!-- Advanced Options Card -->
                <b-card class="mb-1 border">
                  <h6 class="mb-2 font-weight-bold">
                    {{ $t("dmp.systemOptions.advancedOptions") }}
                  </h6>

                  <b-form-checkbox v-model="systemOptions.latchSupervisory" :disabled="isSaving" class="mb-0">
                    {{ $t("dmp.systemOptions.latchSupervisory") }}
                  </b-form-checkbox>
                </b-card>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, inject, getCurrentInstance, computed, watch } from "@vue/composition-api";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { useToast } from "vue-toastification/composition";
import formValidation from "@core/comp-functions/forms/form-validation";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import store from "@/store";
import { useTemplateMode } from "./useTemplateMode";

export default {
  name: "ConfigSystemOptions",
  components: {
    ValidationObserver,
    ValidationProvider,
    ToastificationContent,
  },
  props: {
    uuid: {
      type: String,
      required: true,
    },
    panelId: {
      type: [Number, String],
      required: true,
    },
    systemOptionsData: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    source: {
      type: String,
      default: "default",
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance();
    const proxy = vm.root.proxy;

    const { t } = useI18nUtils();

    const toast = useToast();
    const { refFormObserver, getValidationState } = formValidation();
    const { isTemplateMode, emitTemplateUpdate } = useTemplateMode(props, emit);

    const isLoading = computed(() => props.loading);
    const isSaving = ref(false);

    const systemOptions = ref({
      armingSystem: "",
      keypadArmedLed: "",
      swingerBypassTrips: "",
      bypassLimit: "",
      weatherZipCode: "",
      primaryProgrammingLang: "",
      secondaryProgrammingLang: "",
      primaryUserLang: "",
      secondaryUserLang: "",
      instantArm: null,
      closingWait: null,
      resetSwingerBypass: null,
      enableKeypadPanicKeys: null,
      enhancedZoneTest: null,
      send16Characters: null,
      useFalseAlarmQuestion: null,
      allowOwnUserCodeChange: null,
      panicSupervision: null,
      occupiedPremises: null,
      hoursFromGTM: "",
      timeChange: null,
      zoneActivityHours: "",
      encryptionSettings: "",
      encryptionPassphrase: "",
      entryDelay1: "",
      entryDelay2: "",
      entryDelay3: "",
      entryDelay4: "",
      crossZoneTime: "",
      zoneRetardDelay: "",
      powerFailDelay: "",
      wirelessHouseCode: "",
      troubleAudible: "",
      detectWirelessJamming: null,
      latchSupervisory: null,
      observeDST: null,
      useCelsiusTemperature: null,
      eolResistor: ""
    });

    const armingSystemOptions = computed(() => [
      { value: "AREA", text: "Area" },
      { value: "ALL", text: "All Perimeter" },
      { value: "HOME", text: "Home/Sleep/Away" },
      { value: "GUEST", text: "HSA with Guest" },
    ]);

    const keypadArmedLedOptions = computed(() => [
      { value: "ANY", text: t("dmp.systemOptions.keypadLedOptions.any") },
      { value: "ALL", text: t("dmp.systemOptions.armingTypes.all") },
    ]);

    const programmingLangOptions = computed(() => [
      { value: "NONE", text: t("dmp.systemOptions.languages.none") },
      { value: "ENGLISH", text: t("dmp.systemOptions.languages.english") },
      { value: "SPANISH", text: t("dmp.systemOptions.languages.spanish") },
      { value: "FRENCH", text: t("dmp.systemOptions.languages.french") },
    ]);

    const troubleAudibleOptions = computed(() => [
      { value: "ANY", text: t("dmp.systemOptions.troubleAudibleOptions.any") },
      { value: "DAY", text: t("dmp.systemOptions.troubleAudibleOptions.day") },
      { value: "MINIMUM", text: t("dmp.systemOptions.troubleAudibleOptions.minimum") },
    ]);

    const eolResistorOptions = computed(() => [
      { value: "1K", text: "1K" },
      { value: "2.2K", text: "2.2K" },
    ]);

    const timeChangeOptions = computed(() => [
      { value: "RECEIVER", text: t("dmp.systemOptions.timeChangeOptions.receiver") },
      { value: "NOCHANGE", text: t("dmp.systemOptions.timeChangeOptions.noChange") },
      { value: "SECURECOM", text: t("dmp.systemOptions.timeChangeOptions.secureCom") },
    ]);

    const encryptionSettingsOptions = computed(() => [
      { value: "NONE", text: t("dmp.systemOptions.encryptionSettingsOptions.none") },
      { value: "ALL", text: t("dmp.systemOptions.encryptionSettingsOptions.all") },
      { value: "BOTH", text: t("dmp.systemOptions.encryptionSettingsOptions.both") },
    ]);

    // Helper function to convert Y/N to boolean
    const yesNoToBoolean = (value) => {
      return value === "Y";
    };

    // Helper function to convert boolean to Y/N
    const booleanToYesNo = (value) => {
      return value ? "Y" : "N";
    };

    // Helper function to convert numeric code to language
    const codeToLanguage = (code) => {
      const validValues = ["NONE", "ENGLISH", "SPANISH", "FRENCH", "CZECH", "GREEK", "DUTCH"];
      if (validValues.includes(code)) return code;
      const languageMap = { 0: "NONE", "0": "NONE", 1: "ENGLISH", "1": "ENGLISH", 2: "SPANISH", "2": "SPANISH", 3: "FRENCH", "3": "FRENCH" };
      return languageMap[String(code)] || "NONE";
    };

    // Helper function to convert language to numeric code
    const languageToCode = (language) => {
      const codeMap = {
        NONE: "0",
        ENGLISH: "1",
        SPANISH: "2",
        FRENCH: "3",
        CZECH: "4",
        GREEK: "5",
        DUTCH: "6",
      };
      return codeMap[language] || "0";
    };

    // Helper function to convert keypad LED code to text
    const codeToKeypadLed = (code) => {
      const ledMap = {
        0: "ALL",
        1: "ANY",
      };
      return ledMap[String(code)] || "ANY";
    };

    // Helper function to convert keypad LED text to code
    const keypadLedToCode = (led) => {
      const codeMap = {
        ALL: "0",
        ANY: "1",
      };
      return codeMap[led] || "0";
    };

    // Helper function to convert trouble audible code to text
    const codeToTroubleAudible = (code) => {
      const audibleMap = {
        0: "ANY",
        1: "DAY",
        2: "MINIMUM",
      };
      return audibleMap[String(code)] || "DAY";
    };

    // Helper function to convert trouble audible text to code
    const troubleAudibleToCode = (audible) => {
      const codeMap = {
        ANY: "0",
        DAY: "1",
        MINIMUM: "2",
      };
      return codeMap[audible] || "1";
    };

    // Helper function to convert arming system code to text
    const codeToArmingSystem = (code) => {
      const armingMap = {
        N: "AREA",
        A: "ALL",
        G: "GUEST",
        H: "HOME",
      };
      return armingMap[code] || "AREA";
    };

    // Helper function to convert arming system text to code
    const armingSystemToCode = (system) => {
      const codeMap = {
        AREA: "N",
        ALL: "A",
        GUEST: "G",
        HOME: "H",
      };
      return codeMap[system] || "N";
    };

    const eolResistorToCode = (eol) => {
      const eolMap = {
        "1K": "1",
        "2.2K": "2",
      };
      return eolMap[eol] || "1"
    }

    const codeToeolResistor = (eol) => {
      const codeMap = {
        "1": "1K",
        "2": "2.2K",
      };
      return codeMap[eol] || "1K"
    }


    const codeToTimeChange = (time) => {
      const codeMap = {
        "Y": "RECEIVER",
        "N": "NOCHANGE",
        "S": "SECURECOM",
      };
      return codeMap[time] || "RECEIVER"
    }

    const codeToEncryptionSettings = (code) => {
      const map = {
        "N": "NONE",
        "A": "ALL",
        "B": "BOTH",
      };
      return map[code] || "NONE";
    };


    // Helper function to pad numbers with zeros
    const padNumber = (value, length) => {
      return String(value).padStart(length, "0");
    };

    // Helper function to parse string numbers with leading zeros
    const parseNumericString = (value) => {
      if (value === null || value === undefined || value === "") {
        return "";
      }
      const parsed = parseInt(value, 10);
      if (isNaN(parsed)) {
        return "";
      }
      return parsed;
    };

    const loadSystemOptions = () => {
      if (!props.systemOptionsData || Object.keys(props.systemOptionsData).length === 0) {
        return;
      }

      try {
        const apiData = props.systemOptionsData;

        systemOptions.value = {
          armingSystem: codeToArmingSystem(apiData.armingSystem),
          keypadArmedLed: codeToKeypadLed(apiData.keypadArmedLed),
          swingerBypassTrips: parseNumericString(apiData.swingerBypassTrips),
          bypassLimit: parseNumericString(apiData.bypassLimit),
          weatherZipCode: apiData.weatherZipCode?.trim() || "",
          primaryProgrammingLang: codeToLanguage(apiData.primaryProgrammingLang),
          secondaryProgrammingLang: codeToLanguage(apiData.secondaryProgrammingLang),
          primaryUserLang: codeToLanguage(apiData.primaryUserLang),
          secondaryUserLang: codeToLanguage(apiData.secondaryUserLang),
          instantArm: yesNoToBoolean(apiData.instantArm),
          closingWait: yesNoToBoolean(apiData.closingWait),
          resetSwingerBypass: yesNoToBoolean(apiData.resetSwingerBypass),
          enableKeypadPanicKeys: yesNoToBoolean(apiData.enableKeypadPanicKeys),
          enhancedZoneTest: yesNoToBoolean(apiData.enhancedZoneTest),
          send16Characters: yesNoToBoolean(apiData.send16Characters),
          useFalseAlarmQuestion: yesNoToBoolean(apiData.useFalseAlarmQuestion),
          allowOwnUserCodeChange: yesNoToBoolean(apiData.allowOwnUserCodeChange),
          panicSupervision: yesNoToBoolean(apiData.panicSupervision),
          occupiedPremises: yesNoToBoolean(apiData.occupiedPremises),
          hoursFromGTM: parseNumericString(apiData.hoursFromGTM),
          timeChange: codeToTimeChange(apiData.timeChange),
          zoneActivityHours: parseNumericString(apiData.zoneActivityHours),
          encryptionSettings: codeToEncryptionSettings(apiData.encryptionSettings),
          encryptionPassphrase: apiData.encryptionPassphrase || "",
          entryDelay1: parseNumericString(apiData.entryDelay1),
          entryDelay2: parseNumericString(apiData.entryDelay2),
          entryDelay3: parseNumericString(apiData.entryDelay3),
          entryDelay4: parseNumericString(apiData.entryDelay4),
          crossZoneTime: parseNumericString(apiData.crossZoneTime),
          zoneRetardDelay: parseNumericString(apiData.zoneRetardDelay),
          powerFailDelay: parseNumericString(apiData.powerFailDelay),
          wirelessHouseCode: parseNumericString(apiData.wirelessHouseCode),
          troubleAudible: codeToTroubleAudible(apiData.troubleAudible),
          detectWirelessJamming: yesNoToBoolean(apiData.detectWirelessJamming),
          latchSupervisory: yesNoToBoolean(apiData.latchSupervisory),
          observeDST: yesNoToBoolean(apiData.observeDST),
          useCelsiusTemperature: yesNoToBoolean(apiData.useCelsiusTemperature),
          eolResistor: codeToeolResistor(apiData.eolResistor)
        };
      } catch (error) {
        console.error("Error loading system options:", error);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: proxy.$t("dmp.systemOptions.messages.loadError"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      }
    };

    const buildTemplateItem = () => {
      const dataToSend = {
        closingWait: booleanToYesNo(systemOptions.value.closingWait),
        entryDelay1: padNumber(systemOptions.value.entryDelay1, 3),
        entryDelay2: padNumber(systemOptions.value.entryDelay2, 3),
        entryDelay3: padNumber(systemOptions.value.entryDelay3, 3),
        entryDelay4: padNumber(systemOptions.value.entryDelay4, 3),
        crossZoneTime: padNumber(systemOptions.value.crossZoneTime, 3),
        zoneRetardDelay: padNumber(systemOptions.value.zoneRetardDelay, 3),
        send16Characters: booleanToYesNo(systemOptions.value.send16Characters),
        swingerBypassTrips: String(systemOptions.value.swingerBypassTrips),
        resetSwingerBypass: booleanToYesNo(systemOptions.value.resetSwingerBypass),
        timeChange: systemOptions.value.timeChange,
        hoursFromGTM: padNumber(systemOptions.value.hoursFromGTM, 2),
        latchSupervisory: booleanToYesNo(systemOptions.value.latchSupervisory),
        powerFailDelay: padNumber(systemOptions.value.powerFailDelay, 2),
        primaryProgrammingLang: systemOptions.value.primaryProgrammingLang,
        secondaryProgrammingLang: systemOptions.value.secondaryProgrammingLang,
        primaryUserLang: systemOptions.value.primaryUserLang,
        secondaryUserLang: systemOptions.value.secondaryUserLang,
        bypassLimit: String(systemOptions.value.bypassLimit),
        wirelessHouseCode: padNumber(systemOptions.value.wirelessHouseCode, 3),
        detectWirelessJamming: booleanToYesNo(
          systemOptions.value.detectWirelessJamming
        ),
        enableKeypadPanicKeys: booleanToYesNo(
          systemOptions.value.enableKeypadPanicKeys
        ),
        armingSystem: systemOptions.value.armingSystem,
        occupiedPremises: booleanToYesNo(systemOptions.value.occupiedPremises),
        troubleAudible: systemOptions.value.troubleAudible,
        enhancedZoneTest: booleanToYesNo(systemOptions.value.enhancedZoneTest),
        instantArm: booleanToYesNo(systemOptions.value.instantArm),
        keypadArmedLed: systemOptions.value.keypadArmedLed,
        useFalseAlarmQuestion: booleanToYesNo(
          systemOptions.value.useFalseAlarmQuestion
        ),
        allowOwnUserCodeChange: booleanToYesNo(
          systemOptions.value.allowOwnUserCodeChange
        ),
        panicSupervision: booleanToYesNo(systemOptions.value.panicSupervision),
        weatherZipCode: String(systemOptions.value.weatherZipCode || ""),
        zoneActivityHours: String(systemOptions.value.zoneActivityHours),
        encryptionSettings: systemOptions.value.encryptionSettings,
        encryptionPassphrase: systemOptions.value.encryptionPassphrase,
        observeDST: booleanToYesNo(systemOptions.value.observeDST),
        useCelsiusTemperature: booleanToYesNo(systemOptions.value.useCelsiusTemperature),
        eolResistor: systemOptions.value.eolResistor,
        dualEolResistor: 'N',
      };
      return { ...(props.systemOptionsData || {}), ...dataToSend };
    };

    const validateTemplateSection = async () =>
      refFormObserver.value ? await refFormObserver.value.validate() : true;

    const saveSystemOptions = async () => {
      const isValid = await refFormObserver.value.validate();
      if (!isValid) {
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.systemOptions.messages.validationError"),
            text: proxy.$t("dmp.systemOptions.messages.validationErrorText"),
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }

      isSaving.value = true;
      store.commit("dmp_panels/setOperationActive", true);
      try {
        const dataToSend = {
          closingWait: booleanToYesNo(systemOptions.value.closingWait),
          entryDelay1: padNumber(systemOptions.value.entryDelay1, 3),
          entryDelay2: padNumber(systemOptions.value.entryDelay2, 3),
          entryDelay3: padNumber(systemOptions.value.entryDelay3, 3),
          entryDelay4: padNumber(systemOptions.value.entryDelay4, 3),
          crossZoneTime: padNumber(systemOptions.value.crossZoneTime, 3),
          zoneRetardDelay: padNumber(systemOptions.value.zoneRetardDelay, 3),
          send16Characters: booleanToYesNo(systemOptions.value.send16Characters),
          swingerBypassTrips: String(systemOptions.value.swingerBypassTrips),
          resetSwingerBypass: booleanToYesNo(systemOptions.value.resetSwingerBypass),
          timeChange: systemOptions.value.timeChange,
          hoursFromGTM: padNumber(systemOptions.value.hoursFromGTM, 2),
          latchSupervisory: booleanToYesNo(systemOptions.value.latchSupervisory),
          powerFailDelay: padNumber(systemOptions.value.powerFailDelay, 2),
          primaryProgrammingLang: systemOptions.value.primaryProgrammingLang,
          secondaryProgrammingLang: systemOptions.value.secondaryProgrammingLang,
          primaryUserLang: systemOptions.value.primaryUserLang,
          secondaryUserLang: systemOptions.value.secondaryUserLang,
          bypassLimit: String(systemOptions.value.bypassLimit),
          wirelessHouseCode: padNumber(systemOptions.value.wirelessHouseCode, 3),
          detectWirelessJamming: booleanToYesNo(
            systemOptions.value.detectWirelessJamming
          ),
          enableKeypadPanicKeys: booleanToYesNo(
            systemOptions.value.enableKeypadPanicKeys
          ),
          armingSystem: systemOptions.value.armingSystem,
          occupiedPremises: booleanToYesNo(systemOptions.value.occupiedPremises),
          troubleAudible: systemOptions.value.troubleAudible,
          enhancedZoneTest: booleanToYesNo(systemOptions.value.enhancedZoneTest),
          instantArm: booleanToYesNo(systemOptions.value.instantArm),
          keypadArmedLed: systemOptions.value.keypadArmedLed,
          useFalseAlarmQuestion: booleanToYesNo(
            systemOptions.value.useFalseAlarmQuestion
          ),
          allowOwnUserCodeChange: booleanToYesNo(
            systemOptions.value.allowOwnUserCodeChange
          ),
          panicSupervision: booleanToYesNo(systemOptions.value.panicSupervision),
          weatherZipCode: String(systemOptions.value.weatherZipCode || ""),
          zoneActivityHours: String(systemOptions.value.zoneActivityHours),
          encryptionSettings: systemOptions.value.encryptionSettings,
          encryptionPassphrase: systemOptions.value.encryptionPassphrase,
          observeDST: booleanToYesNo(systemOptions.value.observeDST),
          useCelsiusTemperature: booleanToYesNo(systemOptions.value.useCelsiusTemperature),
          eolResistor: systemOptions.value.eolResistor,
          dualEolResistor: 'N',
        };

        if (isTemplateMode.value) {
          const item = { ...(props.systemOptionsData || {}), ...dataToSend };
          emitTemplateUpdate("system_options", item);
          toast({
            component: ToastificationContent,
            props: {
              title: proxy.$t("dmp.common.success"),
              text: proxy.$t("dmp.systemOptions.messages.saveSuccess"),
              icon: "CheckIcon",
              variant: "success",
            },
          });
          store.commit("dmp_panels/setOperationActive", false);
          isSaving.value = false;
          return;
        }

        const response = await store.dispatch("dmp_panels/setSystemOptions", {
          uuid: props.uuid,
          data: dataToSend,
        });

        if (response.success) {

          toast({
            component: ToastificationContent,
            props: {
              title: proxy.$t("dmp.common.success"),
              text: proxy.$t("dmp.systemOptions.messages.saveSuccess"),
              icon: "CheckIcon",
              variant: "success",
            },
          });

          emit("operation-completed");
        } else {
          throw new Error(response.message || "Failed to save system options");
        }
      } catch (error) {
        console.error("Error saving system options:", error);
        emit("operation-error", error);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: error.message || proxy.$t("dmp.systemOptions.messages.saveError"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        store.commit("dmp_panels/setOperationActive", false);
        isSaving.value = false;
      }
    };

    watch(
      () => props.systemOptionsData,
      (newData) => {
        if (newData && Object.keys(newData).length > 0) {
          loadSystemOptions();
        }
      },
      { deep: true }
    );

    const registerTemplateSection = inject("registerTemplateSection", null);
    const unregisterTemplateSection = inject("unregisterTemplateSection", null);

    onMounted(() => {
      loadSystemOptions();
      if (isTemplateMode.value && registerTemplateSection) {
        registerTemplateSection("system_options", {
          validate: validateTemplateSection,
          collect: buildTemplateItem,
        });
      }
    });

    onBeforeUnmount(() => {
      if (isTemplateMode.value && unregisterTemplateSection) {
        unregisterTemplateSection("system_options");
      }
    });

    return {
      isLoading,
      systemOptions,
      isSaving,
      armingSystemOptions,
      keypadArmedLedOptions,
      programmingLangOptions,
      troubleAudibleOptions,
      saveSystemOptions,
      refFormObserver,
      getValidationState,
      toast,
      t,
      eolResistorOptions,
      timeChangeOptions,
      encryptionSettingsOptions,
      isTemplateMode,
    };
  },
};
</script>

<style scoped lang="scss">
.border {
  border: 1px solid #ebe9f1;
}

.font-weight-bold {
  font-weight: 600;
}

// Dark mode adjustments
.dark-layout {
  .border {
    border-color: #404656;
  }
}
</style>
